import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import Landing from '../pages/Landing';
import LandingHeader from './LandingHeader';
import Footer from './Footer';
import useSWR from 'swr';
import Fetcher from '../lib/Fetcher';
import HttpInterceptor from '../lib/HttpInterceptor';

/**
 * Component to show landing page on root path "/"
 * Behavior:
 * - In browser mode: Always show landing page (even if user is logged in)
 * - In standalone mode (PWA/"open in app"): If user is logged in, redirect to their dashboard
 *   Otherwise, show landing page
 */
const HomeRedirect = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [retryCount, setRetryCount] = useState(0);

  const detectStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  // Initialize from a synchronous check to avoid "Loading..." flash in standalone mode
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  
  const [manualSession, setManualSession] = useState(null);
  
  // Check if app is opened in standalone mode (PWA)
  useEffect(() => {
    const updateStandalone = () => setIsStandalone(detectStandalone());

    updateStandalone();
    window.addEventListener('resize', updateStandalone);
    return () => window.removeEventListener('resize', updateStandalone);
  }, []);

  // In browser mode: always show landing page (no session fetch, no loading flash).
  // In standalone mode: fetch session to optionally redirect to dashboard.
  const swrKey = isStandalone ? '/auth/session' : null;

  // Configure SWR to not retry on 401 errors (we'll handle retries manually in standalone mode)
  const { data: session, error, isLoading, mutate } = useSWR(swrKey, Fetcher, {
    shouldRetryOnError: (error) => {
      // Don't retry on 401 - it's expected when not logged in
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry on other errors (network issues, etc.)
      return true;
    },
    errorRetryCount: 0,
    revalidateOnFocus: false,
    revalidateOnReconnect: false, // Disable reconnect revalidation to prevent unnecessary requests
    dedupingInterval: 5000, // Dedupe requests within 5 seconds
  });

  // Manual retry for session check ONLY in standalone mode when we get 401
  // Limit to 1 retry only (not 3) to reduce console noise
  useEffect(() => {
    // Only retry once in standalone mode (PWA opened from "open in app")
    // This handles the case where cookies might not be loaded yet
    if (isStandalone && error?.response?.status === 401 && retryCount < 1 && !isLoading && !session) {
      // Single retry after 1 second delay
      const retryTimer = setTimeout(async () => {
        try {
          const response = await HttpInterceptor.get('/auth/session', {
            showErrorToast: false,
            // @ts-ignore - suppress console errors for expected 401
            suppressConsoleError: true
          });
          if (response.data && response.data.role) {
            setManualSession(response.data);
            // Update SWR cache
            mutate(response.data, false);
          } else {
            // Stop retrying after first attempt
            setRetryCount(1);
          }
        } catch {
          // Silently handle 401 errors (expected when not logged in)
          // Stop retrying on 401
          setRetryCount(1); // Set to stop retries
        }
      }, 1000); // Single 1 second delay

      return () => clearTimeout(retryTimer);
    }
  }, [isStandalone, error, retryCount, isLoading, mutate, session]);

  // Clear manualSession if we have a definitive error and not in standalone mode
  useEffect(() => {
    // In browser mode, if we get 401, clear any stale manualSession
    if (!isStandalone && error?.response?.status === 401 && manualSession) {
      setManualSession(null);
      setRetryCount(0);
    }
    // If we have a valid session from SWR, clear manualSession (SWR is the source of truth)
    if (session && session.role) {
      setManualSession(null);
      setRetryCount(0);
    }
  }, [isStandalone, error, session, manualSession]);

  // Use SWR session as primary source, manualSession only in standalone mode when SWR fails
  const currentSession = (isStandalone && !session && manualSession) ? manualSession : session;

  // Clear URL parameters that shouldn't be on the home page
  // (trainerId, programId should only be on specific pages)
  useEffect(() => {
    const trainerId = searchParams.get('trainerId');
    const programIds = searchParams.getAll('programId');
    
    // If there are trainer/program params on home page, clear them
    // These params should only be used during auth flow, not on home page
    if (trainerId || programIds.length > 0) {
      const newParams = new URLSearchParams();
      // Keep other params if needed, but remove trainerId and programId
      searchParams.forEach((value, key) => {
        if (key !== 'trainerId' && key !== 'programId') {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Note: We intentionally do NOT block rendering with a full-screen "Loading..." on "/".
  // This prevents an initial flash on mobile/browser and avoids the impression of a reload loop.

  // Only redirect to dashboard if:
  // 1. User is authenticated AND
  // 2. App is in standalone mode (PWA or "open in app")
  // In browser mode, always show landing page (even if logged in)
  if (isStandalone && currentSession && currentSession.role) {
    // Determine dashboard based on role
    if (currentSession.role === 'admin' || currentSession.role === 'super_admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (currentSession.isTrainer && currentSession.trainerStatus === 'active') {
      // Determine trainer type and redirect accordingly
      const trainerServices = (currentSession.services && currentSession.services.length > 0) ? currentSession.services : (currentSession.service ? [currentSession.service] : []);
      const servicePartnerTypes = currentSession.servicePartnerTypes || {};
      
      // Check if trainer has instructor and/or consultant services
      const hasInstructorServices = trainerServices.some(service => 
        (servicePartnerTypes[service] || 'instructor') === 'instructor'
      );
      const hasConsultantServices = trainerServices.some(service => 
        (servicePartnerTypes[service] || 'instructor') === 'consultant'
      );
      
      // If only consultant, redirect to consultant dashboard
      if (hasConsultantServices && !hasInstructorServices) {
        return <Navigate to="/trainer/consultant-dashboard" replace />;
      }
      // If only instructor, redirect to instructor dashboard
      else if (hasInstructorServices && !hasConsultantServices) {
        return <Navigate to="/trainer/home" replace />;
      }
      // If both, check localStorage for preference, default to instructor
      else if (hasInstructorServices && hasConsultantServices) {
        const dashboardPreference = localStorage.getItem('dashboardPreference') || 'instructor';
        if (dashboardPreference === 'consultant') {
          return <Navigate to="/trainer/consultant-dashboard" replace />;
        } else {
          return <Navigate to="/trainer/home" replace />;
        }
      }
      // Fallback to instructor dashboard
      else {
        return <Navigate to="/trainer/home" replace />;
      }
    } else if (currentSession.role === 'customer') {
      return <Navigate to="/orders" replace />;
    } else {
      return <Navigate to="/student/home" replace />;
    }
  }

  // In browser mode OR if not authenticated, show landing page
  // This allows logged-in users to view the landing page in browser
  return (
    <>
      <LandingHeader />
      <main className="flex-grow"><Landing /></main>
      <Footer />
    </>
  );
};

export default HomeRedirect;

